import { Row } from '@amzn/stencil-react-components/layout';
import { token } from '@amzn/stencil-design-tokens/js/web/utils';

interface MetadataGridProps {
  /** Minimum width of each cell before wrapping. */
  minCellWidth?: string;
  /**
   * Most columns to use. Set this on a wide surface, where letting the grid
   * fit as many `minCellWidth` cells as the width allows puts every cell on
   * one row.
   *
   * A ceiling, not a fixed count: the grid still drops to fewer columns when
   * this many will not fit at `minCellWidth`, so a desktop panel narrowed to a
   * phone reflows rather than squashing every cell.
   */
  columns?: number;
  children: React.ReactNode;
}

/** Default minimum cell width for the metadata grid. 140px fits ~2 cells on mobile. */
const DEFAULT_MIN_CELL_WIDTH = '140px'; // standards-ignore: no Stencil token at this scale

const GRID_GAP = 'dimensions.spacing.300';

/**
 * Responsive auto-fill grid for label/value metadata pairs.
 * Used in summary cards, detail expanders, settings panels.
 *
 * Automatically wraps cells to fit the available width.
 */
export function MetadataGrid({
  minCellWidth = DEFAULT_MIN_CELL_WIDTH,
  columns,
  children,
}: MetadataGridProps): JSX.Element {
  return (
    <Row
      css={{
        display: 'grid',
        gridTemplateColumns: trackList(minCellWidth, columns),
        gap: token(GRID_GAP),
      }}
    >
      {children}
    </Row>
  );
}

/**
 * The `grid-template-columns` value for a cell minimum and an optional column ceiling.
 *
 * Both forms use `auto-fill`, so the column count always answers to the width on hand.
 * The ceiling works by raising each track's floor to roughly its share of the row — a
 * track at least a third of the row means at most three fit — while keeping
 * `minCellWidth` as the hard floor, so a narrow viewport gets fewer, readable columns
 * instead of `columns` squashed ones.
 *
 * `100% / columns - gap` overstates the gap's share, which can only make tracks smaller
 * than their exact share and so can never admit an extra column at any realistic width.
 */
function trackList(minCellWidth: string, columns?: number): string {
  if (columns === undefined) {
    return `repeat(auto-fill, minmax(${minCellWidth}, 1fr))`;
  }
  const share = `calc(100% / ${columns} - ${token(GRID_GAP)})`;
  return `repeat(auto-fill, minmax(max(${minCellWidth}, ${share}), 1fr))`;
}
